import React from 'react'

export interface ResponsiveProps extends React.HTMLAttributes<HTMLDivElement> {
  /**
   * Show on mobile only
   */
  mobile?: boolean
  /**
   * Show on tablet only
   */
  tablet?: boolean
  /**
   * Show on desktop only
   */
  desktop?: boolean
  /**
   * Show on large screens only
   */
  large?: boolean
  /**
   * Hide on mobile
   */
  hideMobile?: boolean
  /**
   * Hide on tablet
   */
  hideTablet?: boolean
  /**
   * Hide on desktop
   */
  hideDesktop?: boolean
  /**
   * Hide on large screens
   */
  hideLarge?: boolean
  /**
   * Responsive content
   */
  children: React.ReactNode
}

const Responsive = React.forwardRef<HTMLDivElement, ResponsiveProps>(
  (
    {
      mobile = false,
      tablet = false,
      desktop = false,
      large = false,
      hideMobile = false,
      hideTablet = false,
      hideDesktop = false,
      hideLarge = false,
      children,
      className = '',
      ...props
    },
    ref
  ) => {
    const visibilityClasses = []

    // Show only on specific breakpoints
    if (mobile) visibilityClasses.push('block', 'sm:hidden')
    if (tablet) visibilityClasses.push('hidden', 'sm:block', 'lg:hidden')
    if (desktop) visibilityClasses.push('hidden', 'lg:block', 'xl:hidden')
    if (large) visibilityClasses.push('hidden', 'xl:block')

    // Hide on specific breakpoints
    if (hideMobile) visibilityClasses.push('hidden', 'sm:block')
    if (hideTablet) visibilityClasses.push('block', 'sm:hidden', 'lg:block')
    if (hideDesktop) visibilityClasses.push('block', 'lg:hidden', 'xl:block')
    if (hideLarge) visibilityClasses.push('block', 'xl:hidden')

    // Default: show on all breakpoints if no specific visibility is set
    if (!mobile && !tablet && !desktop && !large && !hideMobile && !hideTablet && !hideDesktop && !hideLarge) {
      visibilityClasses.push('block')
    }

    const responsiveClasses = [
      ...visibilityClasses,
      className
    ].join(' ')

    return (
      <div
        ref={ref}
        className={responsiveClasses}
        {...props}
      >
        {children}
      </div>
    )
  }
)

Responsive.displayName = 'Responsive'

export { Responsive }
